import el from "@cypherpotato/el";
import { api } from "../api";
import { currentPath } from "../global-state";
import { runOperation } from "../ui/operation";
import { Button } from "./button";
import { Icon } from "./icon";

const STORAGE_KEY = "avi-assistant-open";
const DASHBOARD_PREFIX = "/dashboard";

export const aviAssistantOpen = el.state.create(false);
export const aviAssistantVisible = el.state.create(false);
const aviAssistantExpanded = el.state.create(false);

let panel;
let frame;
let initialized = false;
let shouldOpenAviAssistantOnInitialize = localStorage.getItem(STORAGE_KEY) === "true";
let loadRequestId = 0;

function isDashboardPath() {
    return currentPath.value?.startsWith(DASHBOARD_PREFIX);
}

function setPanelState(state) {
    panel?.classList.toggle("error", state === "error");
}

async function loadAviSession() {
    const requestId = ++loadRequestId;

    setPanelState(null);
    frame?.removeAttribute("src");

    await runOperation("Loading Avi...", async () => {
        try {
            const response = await api.getJson("api/v1/accounts/avi");
            if (requestId !== loadRequestId || !aviAssistantVisible.value) {
                return;
            }

            const sessionLocation = response?.data?.sessionLocation || response?.data?.SessionLocation;
            if (!sessionLocation) {
                throw new Error("Avi did not return a chat URL.");
            }

            frame.src = sessionLocation;
            setPanelState(null);
        } catch (error) {
            if (requestId === loadRequestId) {
                setPanelState("error");
            }
        }
    });
}

function syncVisibility() {
    const visible = aviAssistantOpen.value && isDashboardPath();

    aviAssistantVisible.value = visible;
    document.body.classList.toggle("avi-assistant-open", visible);
    document.body.classList.toggle("avi-assistant-expanded", visible && aviAssistantExpanded.value);
    panel?.classList.toggle("visible", visible);
    panel?.classList.toggle("expanded", visible && aviAssistantExpanded.value);
}

function createPanel() {
    panel = el("aside.avi-assistant-panel",
        el(".avi-assistant-header",
            el(".avi-assistant-title",
                // Icon("robot-2-line"),
                // el("strong", "Avi Assistant")
            ),
            el(".avi-assistant-actions",
                el.state.listening([aviAssistantExpanded], () =>
                    Button(Icon(aviAssistantExpanded.value ? "collapse-diagonal-line" : "expand-diagonal-line"), {
                        title: aviAssistantExpanded.value ? "Collapse Avi Assistant" : "Expand Avi Assistant",
                        onClick() {
                            aviAssistantExpanded.value = !aviAssistantExpanded.value;
                            syncVisibility();
                        }
                    })
                ),
                Button(Icon("close-line"), {
                    title: "Close Avi Assistant",
                    onClick() {
                        closeAviAssistant();
                    }
                })
            )
        ),
        el(".avi-assistant-body",
            frame = el("iframe", {
                title: "Avi Assistant",
                allow: "clipboard-write; microphone; camera"
            }),
            el(".avi-assistant-error",
                Icon("error-warning-line"),
                el("span", "Could not open Avi."),
                Button(Icon("refresh-line"), "Try again", {
                    onClick() {
                        loadAviSession();
                    }
                })
            )
        )
    );

    document.body.appendChild(panel);
}

export function initializeAviAssistant() {
    if (initialized) {
        syncVisibility();
        if (aviAssistantVisible.value && !frame?.src) {
            loadAviSession();
        }
        return;
    }

    initialized = true;
    createPanel();
    aviAssistantOpen.subscribe(syncVisibility);
    aviAssistantExpanded.subscribe(syncVisibility);
    currentPath.subscribe(() => {
        syncVisibility();
        if (aviAssistantVisible.value && !frame?.src) {
            loadAviSession();
        }
    });
    syncVisibility();

    if (shouldOpenAviAssistantOnInitialize) {
        shouldOpenAviAssistantOnInitialize = false;
        aviAssistantOpen.value = true;
        syncVisibility();
    }

    if (aviAssistantVisible.value) {
        loadAviSession();
    }
}

export function openAviAssistant() {
    initializeAviAssistant();
    aviAssistantOpen.value = true;
    syncVisibility();
    loadAviSession();
}

export function closeAviAssistant() {
    loadRequestId++;
    aviAssistantOpen.value = false;
    localStorage.removeItem(STORAGE_KEY);
    syncVisibility();
}

export function toggleAviAssistant() {
    if (aviAssistantVisible.value) {
        closeAviAssistant();
    } else {
        openAviAssistant();
        localStorage.setItem(STORAGE_KEY, "true");
    }
}
